/**
 * Producer helper for a connection mutation: a native `useMutation` keyed by the connection
 * it targets (`connectionMutationKey`, so consumers can gather it via `useMutationState`)
 * that carries its declarative `ConnectionOperation` as the mutation `variables`.
 *
 * The `mutationFn` performs the real server write. To settle, make `base` reflect server truth before this mutation leaves
 * `pending` — either `invalidateQueries`/refetch, or fold the confirmed operation into base with
 * `setQueryData(key, base => applyOperation(base, ownerId, operation))` (settle-by-fold, as
 * `settleConfirmedOperation` does — no refetch flash). What to avoid is
 * `setQueryData(key, responseBody)`: the mutation's response isn't the query-cache shape.
 *
 * Exercised by `connection-mutation-lifecycle.browser.test.tsx`, which pins the
 * overlay-while-pending + settle contract described above.
 */
import {useCallback} from 'react'

import {useMutation, type UseMutationOptions, type UseMutationResult} from '@github-ui/react-query'

import {type ConnectionOperation, isEntityOperation} from './connection-operations'
import {type ConnectionMutationVariables, connectionMutationKey} from './pending-operations'

export function useConnectionMutation<TData = void, TError = Error, TOnMutateResult = unknown>({
  ownerId,
  connectionPath,
  prepareVariables,
  mutationFn,
  ...options
}: {
  ownerId: string
  connectionPath: string[]
  /** Decorate a new variables value before it enters the mutation cache. */
  prepareVariables?: (variables: ConnectionMutationVariables) => ConnectionMutationVariables
  /** Performs the server write for this operation and resolves when it's committed. */
  mutationFn: (operation: ConnectionOperation, variables: ConnectionMutationVariables) => Promise<TData>
} & Omit<
  UseMutationOptions<TData, TError, ConnectionMutationVariables, TOnMutateResult>,
  'mutationKey' | 'mutationFn'
>): UseMutationResult<TData, TError, ConnectionMutationVariables, TOnMutateResult> {
  // `applyOperations` locates the connection from the operation's own `connectionPath`, so
  // normalise it to the key's path — otherwise a consumer keyed for this connection could
  // apply the operation to a sibling connection.
  const withNormalisedVariables = useCallback(
    (variables: ConnectionMutationVariables): ConnectionMutationVariables => {
      const {operation} = variables
      const normalised = isEntityOperation(operation)
        ? variables
        : {...variables, operation: {...operation, connectionPath}}
      return prepareVariables?.(normalised) ?? normalised
    },
    [connectionPath, prepareVariables],
  )

  const result = useMutation<TData, TError, ConnectionMutationVariables, TOnMutateResult>({
    ...options,
    mutationKey: connectionMutationKey(ownerId, connectionPath),
    mutationFn: variables => mutationFn(variables.operation, variables),
  })

  // Normalise the operation before it enters the pending cache so both the stored variables
  // (read by `useOptimisticData`) and the server write see the key-aligned path.
  // `mutate`/`mutateAsync` are referentially stable, so wrapping them per-call is safe.
  const {mutate, mutateAsync} = result
  const normalisedMutate: typeof mutate = useCallback(
    (variables, mutateOptions) => mutate(withNormalisedVariables(variables), mutateOptions),
    [mutate, withNormalisedVariables],
  )
  const normalisedMutateAsync: typeof mutateAsync = useCallback(
    (variables, mutateOptions) => mutateAsync(withNormalisedVariables(variables), mutateOptions),
    [mutateAsync, withNormalisedVariables],
  )

  return {...result, mutate: normalisedMutate, mutateAsync: normalisedMutateAsync}
}

export type ReorderConnectionParams = {
  dragItemId: string
  dropItemId: string
  isBefore: boolean
}

export function useReorderConnectionMutation<TData = void, TError = Error, TOnMutateResult = unknown>({
  ownerId,
  connectionPath,
  ...options
}: {
  ownerId: string
  connectionPath: string[]
  mutationFn: (operation: ConnectionOperation) => Promise<TData>
} & Omit<
  UseMutationOptions<TData, TError, ConnectionMutationVariables, TOnMutateResult>,
  'mutationKey' | 'mutationFn'
>) {
  const mutation = useConnectionMutation({ownerId, connectionPath, ...options})
  const {mutate} = mutation
  const reorder = useCallback(
    ({dragItemId, dropItemId, isBefore}: ReorderConnectionParams) => {
      mutate({
        operation: {
          type: 'moveInConnection',
          connectionPath,
          nodeId: dragItemId,
          anchor: isBefore ? {before: dropItemId} : {after: dropItemId},
        },
      })
    },
    [connectionPath, mutate],
  )

  return {...mutation, reorder}
}
